import type { FieldError, UsageRange } from './types';

/**
 * 用量时间范围的解析与预设。
 *
 * 前后端共用同一份逻辑，是为了让「本月」这类词在任何地方都指同一个区间 ——
 * 如果前端自己算一个、后端自己算一个，两边只要差一次时区换算，
 * 界面上显示的数字就会和导出的数字对不上，而这种差异极难被发现。
 *
 * 统一用 **UTC**：平台既有的「每日研判额度按 UTC 零点重置」就是这个口径，
 * 用量统计必须跟它一致，否则「额度用完了但用量显示还有余量」会让人非常困惑。
 */

/** 预设范围。`all` 表示不加时间限制。 */
export const USAGE_RANGE_PRESETS = ['this_month', 'last_30_days', 'all'] as const;

export type UsageRangePreset = (typeof USAGE_RANGE_PRESETS)[number];

export const USAGE_RANGE_LABELS: Readonly<Record<UsageRangePreset, string>> = {
  this_month: '本月',
  last_30_days: '最近 30 天',
  all: '全部',
};

function startOfUtcDay(date: Date): string {
  const copy = new Date(date.getTime());
  copy.setUTCHours(0, 0, 0, 0);
  return copy.toISOString();
}

/** 把预设解析成具体的 ISO 区间。`now` 可注入，便于测试。 */
export function presetToRange(preset: UsageRangePreset, now: Date = new Date()): UsageRange {
  if (preset === 'all') return { from: null, to: null };

  if (preset === 'this_month') {
    const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
    return { from: first.toISOString(), to: null };
  }

  // 最近 30 天：含今天在内共 30 个自然日（UTC），所以起点回到 29 天前的那一天 00:00
  const start = new Date(now.getTime());
  start.setUTCDate(start.getUTCDate() - 29);
  return { from: startOfUtcDay(start), to: null };
}

function isIsoTimestamp(value: string): boolean {
  // 只接受完整的 ISO-8601；`Date.parse` 对 "2026-09-01" 这类残缺输入过于宽松，
  // 宽松会让「我以为筛的是 9 月 1 日 0 点」变成实际按 UTC 0 点解释而没人察觉
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/.test(value)) return false;
  return !Number.isNaN(Date.parse(value));
}

/**
 * 校验并归一化请求里的 `from` / `to`。
 *
 * 非法值一律报错而**不是**静默忽略：静默忽略会让人以为筛的是本月、
 * 实际看到的是全部数据，然后据此做出「这个月用量很低」的错误判断。
 */
export function parseUsageRange(from: string | null, to: string | null, now: Date = new Date()): {
  range?: UsageRange;
  errors: FieldError[];
} {
  const errors: FieldError[] = [];

  const normalize = (raw: string | null, field: string): string | null => {
    if (raw === null) return null;
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (!isIsoTimestamp(trimmed)) {
      errors.push({ field, message: '需要完整的 ISO-8601 时间，例如 2026-09-01T00:00:00.000Z' });
      return null;
    }
    return new Date(trimmed).toISOString();
  };

  const normalizedFrom = normalize(from, 'from');
  const normalizedTo = normalize(to, 'to');

  for (const [field, value] of [
    ['from', normalizedFrom],
    ['to', normalizedTo],
  ] as const) {
    if (value === null) continue;
    if (Date.parse(value) > now.getTime()) {
      errors.push({ field, message: '不能晚于当前时间，未来区间里不可能有用量数据' });
    }
  }

  if (normalizedFrom !== null && normalizedTo !== null && normalizedFrom >= normalizedTo) {
    errors.push({ field: 'from', message: '起始时间必须早于结束时间' });
  }

  if (errors.length > 0) return { errors };
  return { range: { from: normalizedFrom, to: normalizedTo }, errors };
}
